import { useQuery } from '@apollo/client';
import { Box, Grid, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import type { PublicCollectionData } from '@mogul/api-client';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { AmbientLight, DirectionLight, GLTFModel } from 'react-3d-viewer';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import { collectionsApi, whitelistApi } from '../../../api';
import { BackHeader } from '../../../components/common/backheader/BackHeader.component';
import { MarketplaceToggle } from '../../../components/common/marketplaceToggler/MarketplaceToggler.component';
import { NFTActionCard } from '../../../components/nft-action-card/NFTAction.component';
import { NftHistoryComponent } from '../../../components/nft-history/NftHistory.component';
import { NftListingComponent } from '../../../components/nft-listing/NftListing.component';
import { NftYourListingComponent } from '../../../components/nft-listing/NftYourListing.component';
import { NftOrders } from '../../../components/nft-orders/NftOrders.component';
import { SearchBar } from '../../../components/search-bar/SearchBar.component';
import { NETWORKS_BY_CHAIN } from '../../../contexts/app-state-context/apollo/apollo.configs';
import { useWeb3State } from '../../../hooks';
import { useCloudfrontNftAsset } from '../../../hooks/cloudfront-hook';
import { useGetNft } from '../../../hooks/nft-api.hook';
import type { Asset } from '../../../lib/graph/MogulMarketplace';
import { AssetType } from '../../../lib/graph/MogulMarketplace';
import assetQuery from '../../../lib/graph/queries/assetQuery';
import { translateIpfsToPinata } from '../../../lib/nfts/nft-catalog.service';
import { initHideOnScrollEffect } from '../../../utils/searchbar.util';
import styles from './NftDetailsPage.module.css';

interface Params {
  chain?: string;
  nftContractAddress: string;
  nftId: string;
}

export const NftDetailsPage: FC = () => {
  const history = useHistory();
  const [isWhitelistAddress, setIsWhitelistAddress] = useState(false);
  const [isWhitelisted, setIsWhitelisted] = useState(true);
  const [whitelistInfoLoading, setWhitelistInfoLoading] = useState(true);
  const [isWhitelistDate, setIsWhitelistDate] = useState(true);
  const [floorPrice, setFloorPrice] = useState<string | undefined>(''); // to avoid NFTAction refactoring ;)
  const [bestOrderPrice, setBestOrderPrice] = useState('');
  const { walletDetails } = useWeb3State();
  const {
    chain,
    nftId,
    nftContractAddress: _nftContractAddress,
  } = useParams<Params>();

  const nftContractAddress = _nftContractAddress.toLowerCase();
  const validChain =
    !window.location.href.includes('https://film3.mogulproductions.com/') ||
    chain === undefined ||
    chain === 'eth' ||
    chain === 'bsc';

  const dbNft = useGetNft(nftContractAddress, nftId);
  const { cloudfrontUrl: imageCloudfront } = useCloudfrontNftAsset(
    dbNft?.image,
  );
  const { cloudfrontUrl: animationCloudfront, mimeType: animationMimetype } =
    useCloudfrontNftAsset(dbNft?.animation);

  // BONDED GLUE
  const title =
    nftContractAddress === '0xabcf857c55cabc91a5b68c0a1eadb3b8e5db3734' &&
    nftId === '1'
      ? 'Bonded - Associate Producer Credit'
      : dbNft?.title ?? '';
  // BONDED GLUE

  const [collection, setCollection] =
    useState<PublicCollectionData | null | undefined>();

  useEffect(() => {
    const getCollectionInfo = async () => {
      const collectionInfo = await collectionsApi.getCollectionByAddress({
        contractAddress: nftContractAddress,
      });
      setCollection(collectionInfo.data.data);
    };
    void getCollectionInfo();
  }, []);

  const { loading, data } = useQuery<{ asset?: Asset }>(assetQuery, {
    variables: { graphId: `${nftContractAddress}/${nftId}` },
    context: { network: collection?.network },
    skip: collection?.network === undefined,
  });

  const image =
    imageCloudfront ??
    translateIpfsToPinata(data?.asset ? data.asset.image : '');
  const animation_url =
    imageCloudfront != null
      ? animationCloudfront
      : translateIpfsToPinata(data?.asset ? data.asset.animation_url : '');

  const goToMarketplace = () => {
    history.push('/platform/marketplace');
  };

  useEffect(() => {
    const getWhitelist = async () => {
      setWhitelistInfoLoading(true);

      if (!walletDetails.address) {
        setWhitelistInfoLoading(false);
        return;
      }

      if (collection === undefined) {
        return;
      }

      const currentDate = Date.now();
      const startDate = new Date(collection?.whitelistStart ?? '');
      const endDate = new Date(collection?.whitelistEnd ?? '');

      if (
        collection?.whitelist === true &&
        ((currentDate > startDate.getTime() &&
          currentDate < endDate.getTime()) ||
          currentDate > endDate.getTime())
      ) {
        setIsWhitelisted(true);
        try {
          await whitelistApi.getByWalletAndCollection({
            contractAddress: collection.contractAddress,
            walletAddress: walletDetails.address,
          });
          setIsWhitelistAddress(true);
        } catch (error) {
          setIsWhitelistAddress(false);
        }
      } else if (
        collection?.whitelist === true &&
        currentDate < endDate.getTime()
      ) {
        setIsWhitelisted(true);
        setIsWhitelistAddress(false);
        setIsWhitelistDate(false);
      } else {
        setIsWhitelisted(false);
      }

      setWhitelistInfoLoading(false);
    };
    void getWhitelist();
  }, [collection, walletDetails.address]);

  const goToCollectionsPage = () => {
    const path = `/platform/collection/${walletDetails.chain.key}/${
      collection?.slug ?? nftContractAddress
    }`;

    history.push(path);
  };

  useEffect(() => initHideOnScrollEffect('searchBarWrap', 0, 0), []);

  useEffect(() => {
    // Set head > meta tags regarding OpenGraph manually.
    // React Helmet appends to end of header, which might not get read by bots
    const openGraphData: { [key: string]: any } = {
      'og:title': dbNft?.title,
      'og:description': dbNft?.description,
      'og:image': image,
      'og:url': window.location,
    };

    for (const property in openGraphData) {
      const headMetaElement = document.querySelector(
        `head>meta[property*="${property}"]`,
      );

      headMetaElement?.setAttribute(
        'content',
        openGraphData[property] as string,
      );
    }
  }, [image, dbNft?.title, dbNft?.description]);

  const isNetworkAppropriate = walletDetails.chain.type === collection?.network;

  if (!loading && !data?.asset) {
    return (
      <Box className="w-full h-full items-center justify-center flex flex-col flex-wrap text-center">
        <Typography variant="h1">The asset could not be found!</Typography>
        <Typography variant="h1">Try changing networks</Typography>
        <div className="pt-10">
          <MarketplaceToggle open={true} />
        </div>
      </Box>
    );
  }

  if (!validChain) {
    return (
      <Box className="w-full h-full items-center justify-center flex flex-col flex-wrap text-center">
        <Typography variant="h1">
          {`Chain ${chain} does not exist. Please use either eth or bsc.`}
        </Typography>
      </Box>
    );
  }

  return (
    <div
      className={`${styles.nftDetailsPageContainer} w-full flex flex-col items-center`}
    >
      <div
        className={`h-full flex flex-col sm:grid place-items-center w-screen sm:w-auto`}
      >
        <div
          className={
            styles.searchBarWrap +
            ' sticky top-0 w-full flex justify-center items-center md:py-3 sm:pt-4 -mt-3 sm:-mt-0 sm:pb-3 z-20'
          }
          id={'searchBarWrap'}
        >
          <SearchBar
            tailwindOverride={'lg:w-10/12 sm:w-10/12 w-full'}
            viewableHeightDesktop={0}
            viewableHeightMobile={0}
          />
        </div>
        <div
          className={`${styles.mediaBackground} w-full h-full flex flex-col justify-center sm:py-10 sm:pt-0 py-20 pt-8 place-items-center items-center`}
        >
          <div className="px-5 lg:px-32">
            <Box className="pt-0">
              <BackHeader
                title="back to marketplace"
                headerOnPress={goToMarketplace}
              />
            </Box>
            <div className="mt-5 sm:mt-10 mb-14 h-full">
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                  <div className="flex justify-center xl:justify-start h-full">
                    {loading ? (
                      <Skeleton
                        variant="rect"
                        className="px-20 mt-12 mb-14"
                        width="50%"
                        height="400px"
                      />
                    ) : animationMimetype === 'model/gltf-binary' ? (
                      <div className="flex flex-col items-center xl:items-start gap-3 ">
                        <Typography variant="h6" className="w-2/3 xl:w-2/3">
                          This is a 3D model. Try to zoom and rotate the model
                          below!
                        </Typography>
                        <div className="grid place-content-center h-full w-full">
                          <GLTFModel
                            rotation={{ x: 0, y: -300, z: 0 }}
                            width={380}
                            height={380}
                            position={{ x: 0, y: 0, z: -100 }}
                            src={animation_url}
                          >
                            <AmbientLight color={0xffffff} />
                            <DirectionLight
                              color={0xffffff}
                              position={{ x: 100, y: 200, z: 100 }}
                            />
                            <DirectionLight
                              color={0xff00ff}
                              position={{ x: -100, y: 200, z: -100 }}
                            />
                          </GLTFModel>
                        </div>
                      </div>
                    ) : animationMimetype === 'image/gif' ? (
                      <img
                        className="w-1/2 xl:w-4/5 sm:max-w-md h-full"
                        src={animation_url ?? ''}
                        alt="nft_gif"
                      />
                    ) : animation_url ? (
                      <div className="flex flex-col place-content-center h-full w-full">
                        <video
                          className="w-full"
                          src={animation_url}
                          autoPlay
                          loop
                          muted
                          controls
                        />
                      </div>
                    ) : (
                      image && (
                        <img
                          className="w-1/2 xl:w-4/5 sm:max-w-md h-full"
                          src={image}
                          alt="nft"
                        />
                      )
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                  <Grid container spacing={5}>
                    <Grid
                      item
                      xs={12}
                      className={
                        'align-text-top align-top text-center xl:text-left'
                      }
                    >
                      <div className="inline-block align-baseline">
                        <h1
                          className={`${styles.nftTitle} align-text-top align-top text-5xl pb-2`}
                        >
                          {title}
                        </h1>
                      </div>
                      {collection?.name && (
                        <div className={`${styles.collection}`}>
                          Collection:{' '}
                          <button
                            onClick={goToCollectionsPage}
                            className={`${styles.collectionLink}`}
                          >
                            {' '}
                            {collection.name}
                          </button>
                        </div>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <div className="w-full">
                        {data?.asset && isNetworkAppropriate ? (
                          <NFTActionCard
                            {...data.asset}
                            isWhitelisted={
                              isWhitelisted && !whitelistInfoLoading
                            }
                            isWhitelistAddress={
                              isWhitelistAddress && !whitelistInfoLoading
                            }
                            isWhitelistDate={isWhitelistDate}
                            setFloorPrice={setFloorPrice}
                            bestOrderPrice={bestOrderPrice}
                            image={image}
                            dbNft={dbNft}
                            collection={collection}
                            goToCollectionsPage={goToCollectionsPage}
                          />
                        ) : collection?.network ? (
                          `Switch the network to ${
                            NETWORKS_BY_CHAIN.eth.includes(collection.network)
                              ? 'Ethereum'
                              : 'Binance Smart Chain'
                          } to interact`
                        ) : null}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
        <Box className="w-full">
          <div className="mx-4 xl:w-full xl:mx-auto">
            <Grid container className="lg:px-32 py-10">
              <div className="w-full">
                <div className={`${styles.descriptionTitle} pb-1 text-3xl`}>
                  Description
                </div>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: 16,
                  }}
                >
                  {dbNft?.description ?? ''}
                </Typography>
              </div>
            </Grid>
            {isNetworkAppropriate && (
              <>
                <Box className="lg:px-32 xl:mb-24 mb-32 mt-10 md:mt-0">
                  <NftYourListingComponent
                    nftContractAddress={nftContractAddress}
                    nftId={nftId}
                    walletAddress={walletDetails.address?.toLowerCase() ?? ''}
                    type={data?.asset?.type ?? AssetType.Erc1155}
                  />
                </Box>

                <Box className="lg:px-32 xl:mb-24 mb-32 mt-10 md:mt-0">
                  <NftListingComponent
                    nftContractAddress={nftContractAddress}
                    nftId={nftId}
                    walletAddress={walletDetails.address?.toLowerCase() ?? ''}
                    type={data?.asset?.type ?? AssetType.Erc1155}
                    isWhitelisted={isWhitelisted && !whitelistInfoLoading}
                    isWhitelistAddress={
                      isWhitelistAddress && !whitelistInfoLoading
                    }
                  />
                </Box>

                <Box className="lg:px-32 xl:mb-24 mb-32 mt-10 md:mt-0">
                  <NftOrders
                    nftContractAddress={nftContractAddress}
                    nftId={nftId}
                    walletAddress={walletDetails.address?.toLowerCase() ?? ''}
                    asset={data?.asset}
                    floorPrice={floorPrice}
                    setBestOrderPrice={setBestOrderPrice}
                  />
                </Box>

                <Box className="lg:px-32">
                  <NftHistoryComponent
                    nftContractAddress={nftContractAddress}
                    nftId={nftId}
                  />
                </Box>
              </>
            )}
          </div>
        </Box>
      </div>
    </div>
  );
};
